import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { KeyRound, Loader2, CheckCircle2, Wifi, QrCode, X } from "lucide-react";
import jsQR from "jsqr";
import { useField } from "../FieldContext";
import { Mono, Btn } from "../ui";
import { formatApiErrorDetail } from "@/lib/api";

export default function Connect() {
  const { store, org, online, sync, bump } = useField();
  const [params] = useSearchParams();
  const [code, setCode] = useState((params.get("code") || "").toUpperCase().slice(0, 6));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [scanning, setScanning] = useState(false);
  const autoRef = useRef(false);
  const nav = useNavigate();

  const join = async (joinCode) => {
    if (!online) {
      setErr("You need to be online to connect to a team.");
      return;
    }
    setBusy(true);
    setErr("");
    setMsg("");
    try {
      const data = await store.joinOrg(joinCode);
      setMsg(data.already_member ? `Already connected to ${data.name}.` : `Connected to ${data.name}.`);
      await sync();
      bump();
      setTimeout(() => nav("/field"), 900);
    } catch (e2) {
      setErr(formatApiErrorDetail(e2?.response?.data?.detail) || "Could not connect. Check the code.");
    } finally {
      setBusy(false);
    }
  };

  // auto-join when arriving from a scanned QR (…/field/connect?code=ABC123)
  useEffect(() => {
    const qr = (params.get("code") || "").toUpperCase();
    if (qr.length === 6 && online && !autoRef.current) {
      autoRef.current = true;
      join(qr);
    }
  }, [online]); // eslint-disable-line

  const submit = (e) => {
    e.preventDefault();
    join(code);
  };

  const onScan = (text) => {
    setScanning(false);
    const m = /[?&]code=([A-Z2-9]{6})/i.exec(text) || /^([A-Z2-9]{6})$/i.exec(text.trim());
    if (m) {
      const c = m[1].toUpperCase();
      setCode(c);
      join(c);
    } else {
      setErr("That QR code doesn't look like an MDAu team code.");
    }
  };

  return (
    <div className="space-y-6" data-testid="field-connect">
      <div>
        <Mono className="text-gold-400 text-[0.6rem]">Team access</Mono>
        <h1 className="font-heading text-2xl mt-1">Connect this device</h1>
        <p className="text-white/50 text-sm mt-1 font-light">
          Currently connected to <span className="text-gold-300">{org?.name || "your own workspace"}</span>. Scan your team's QR code
          or enter the 6-character team code to switch to your council's disaster events.
        </p>
      </div>

      <button onClick={() => { setErr(""); setScanning(true); }} disabled={!online || busy} data-testid="field-scan-qr-btn"
        className="w-full flex items-center justify-center gap-2 bg-gold-500 text-navy-950 font-semibold py-4 rounded-sm hover:bg-gold-400 transition-colors disabled:opacity-50">
        <QrCode className="h-5 w-5" /> Scan team QR code
      </button>

      <div className="flex items-center gap-3">
        <div className="flex-1 border-t border-gold-500/15" />
        <Mono className="text-[0.5rem] text-white/35">or type the code</Mono>
        <div className="flex-1 border-t border-gold-500/15" />
      </div>

      <form onSubmit={submit} className="space-y-4">
        <input
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          maxLength={6}
          placeholder="ABC123"
          data-testid="field-join-code"
          className="w-full bg-navy-950 border border-gold-500/25 px-4 py-5 text-center font-mono tracking-[0.5em] text-3xl text-gold-300 rounded-sm focus:outline-none focus:border-gold-500 uppercase"
        />
        {err && <div className="text-red-300 text-sm" data-testid="field-join-error">{err}</div>}
        {msg && (
          <div className="text-emerald-300 text-sm flex items-center gap-2" data-testid="field-join-success">
            <CheckCircle2 className="h-4 w-4" /> {msg}
          </div>
        )}
        <Btn type="submit" disabled={busy || code.length < 6} testid="field-join-submit">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />} Connect
        </Btn>
      </form>

      {!online && (
        <div className="flex items-center gap-2 text-amber-300 text-xs border border-amber-500/30 bg-amber-500/10 rounded-sm px-3 py-2">
          <Wifi className="h-3.5 w-3.5" /> Offline — reconnect to the internet to join a team.
        </div>
      )}

      {scanning && <QrScanner onResult={onScan} onClose={() => setScanning(false)} />}
    </div>
  );
}

function QrScanner({ onResult, onClose }) {
  const videoRef = useRef(null);
  const [camErr, setCamErr] = useState("");

  useEffect(() => {
    let stream, raf, stopped = false;
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d", { willReadFrequently: true });

    const tick = () => {
      if (stopped) return;
      const v = videoRef.current;
      if (v && v.readyState === v.HAVE_ENOUGH_DATA) {
        canvas.width = v.videoWidth;
        canvas.height = v.videoHeight;
        ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
        const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const hit = jsQR(img.data, img.width, img.height, { inversionAttempts: "dontInvert" });
        if (hit && hit.data) {
          stopped = true;
          onResult(hit.data);
          return;
        }
      }
      raf = requestAnimationFrame(tick);
    };

    navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } })
      .then((s) => {
        stream = s;
        if (videoRef.current) {
          videoRef.current.srcObject = s;
          videoRef.current.setAttribute("playsinline", "true");
          videoRef.current.play();
          raf = requestAnimationFrame(tick);
        }
      })
      .catch(() => setCamErr("Camera unavailable — allow camera access or type the code instead."));

    return () => {
      stopped = true;
      if (raf) cancelAnimationFrame(raf);
      if (stream) stream.getTracks().forEach((t) => t.stop());
    };
  }, [onResult]);

  return (
    <div className="fixed inset-0 bg-navy-950/95 z-50 flex flex-col items-center justify-center p-6" data-testid="field-qr-scanner">
      <button onClick={onClose} data-testid="field-qr-scanner-close" className="absolute top-5 right-5 border border-gold-500/30 rounded-sm p-2 text-white/70">
        <X className="h-5 w-5" />
      </button>
      <Mono className="text-gold-400 text-[0.6rem] mb-4">Point at the QR code in The MDAu Office</Mono>
      <div className="relative w-full max-w-sm aspect-square overflow-hidden rounded-sm border-2 border-gold-500/60">
        <video ref={videoRef} muted className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-8 border border-gold-400/70 rounded-sm pointer-events-none" />
      </div>
      {camErr && <div className="text-amber-300 text-sm mt-4 text-center max-w-sm" data-testid="field-qr-cam-error">{camErr}</div>}
    </div>
  );
}
